import { lazy, Suspense, useMemo, useSyncExternalStore } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import '@vendia/brand-design-system/styles.css';

import { ACCENT, MASTER, vendiaTokens } from './vendia/brand';
import { UI } from './vendia/brand/ui';
import { AppShell } from './vendia/components/AppShell';
import { AuthGate } from './vendia/components/AuthGate';
import { DashboardPage } from './vendia/pages/DashboardPage';
import { CombosPage } from './vendia/pages/CombosPage';
import { ItemsPage } from './vendia/pages/ItemsPage';
import { LocationsPage } from './vendia/pages/LocationsPage';
import { PaymentProvidersPage } from './vendia/pages/PaymentProvidersPage';
import { ReactAdminProbePage } from './vendia/pages/ReactAdminProbePage';
import { ReportViewRoute } from './vendia/pages/ReportViewPage';
import { ReportsPage } from './vendia/pages/ReportsPage';
import { ResourceHealthPage } from './vendia/pages/ResourceHealthPage';
import { SettingsPage } from './vendia/pages/SettingsPage';
import { getRuntimeConfig } from './vendia/runtime';
import { getLocale, subscribeLocale, t } from './vendia/i18n';
import { LoadingBlock } from './vendia/components/StateBlock';

// The fabric.js label designer is heavy and rarely opened: load it on demand.
const LabelDesignPage = lazy(() => import('./vendia/pages/LabelDesignPage').then((m) => ({ default: m.LabelDesignPage })));
import { CrudResourcePage } from './vendia/crud/CrudResourcePage';
import { resourceConfigByName } from './vendia/crud/resourceConfigs';
import { featureFullscreenRoutes, featurePublicRoutes, featureRoutes } from './vendia/features/registry';

// Theme is data-driven from the Brand OS tokens (see vendia/brand/ui.ts). Business
// accent = purple, master brand = navy, neutral surfaces; no gradients, glows or
// blur, weights within the approved 300-700 range, token radii and shadows.
const buildTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: { main: ACCENT[600], light: ACCENT[500], contrastText: MASTER.white },
    secondary: { main: MASTER.navy },
    success: { main: UI.success, contrastText: MASTER.white },
    info: { main: ACCENT[600], contrastText: MASTER.white },
    warning: { main: UI.warning, contrastText: MASTER.white },
    error: { main: UI.danger, contrastText: MASTER.white },
    text: { primary: UI.text, secondary: UI.textSecondary },
    background: { default: UI.surface, paper: UI.white },
    divider: UI.border
  },
  shape: { borderRadius: UI.radiusMd },
  typography: {
    fontFamily: vendiaTokens.typography.fontFamily.primary.join(','),
    fontWeightLight: vendiaTokens.typography.fontWeight.light,
    fontWeightRegular: vendiaTokens.typography.fontWeight.regular,
    fontWeightMedium: vendiaTokens.typography.fontWeight.medium,
    fontWeightBold: vendiaTokens.typography.fontWeight.bold
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: UI.surface,
          color: UI.text
        },
        '::selection': {
          background: UI.accentAlpha(0.18)
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: UI.radiusMd,
          fontWeight: vendiaTokens.typography.fontWeight.semibold
        },
        outlined: {
          borderColor: UI.borderStrong,
          backgroundColor: UI.white
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: UI.radiusMd,
          border: `1px solid ${UI.border}`,
          backgroundColor: UI.white,
          backgroundImage: 'none',
          boxShadow: UI.shadowSm
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: UI.textSecondary,
          fontSize: '0.72rem',
          fontWeight: vendiaTokens.typography.fontWeight.semibold,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          backgroundColor: UI.headerFill
        },
        root: {
          borderBottomColor: UI.border
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: UI.radiusMd,
            backgroundColor: UI.white
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: UI.radiusSm,
          fontWeight: vendiaTokens.typography.fontWeight.semibold
        }
      }
    },
    // Localised table pagination labels (MUI defaults are English-only).
    MuiTablePagination: {
      defaultProps: {
        labelRowsPerPage: t('common.rowsPerPage'),
        labelDisplayedRows: ({ from, to, count }) => t('common.displayedRows', { from, to, count: count === -1 ? `> ${to}` : count })
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: UI.radiusLg,
          border: `1px solid ${UI.border}`,
          boxShadow: UI.shadowMd
        }
      }
    }
  }
});

export default function App() {
  const runtime = getRuntimeConfig();
  const locale = useSyncExternalStore(subscribeLocale, getLocale, getLocale);
  // Rebuilt on locale change so translated component defaults follow the language.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const theme = useMemo(() => buildTheme(), [locale]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter basename={runtime.basePath}>
        <Routes>
          {featurePublicRoutes.map((route) => (
            <Route key={`public:${route.path}`} path={route.path} element={route.element} />
          ))}
          <Route path="*" element={<AuthenticatedApp />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function AuthenticatedApp() {
  return (
        <AuthGate>
          {({ user, onLogout }) => (
            <Routes>
              {featureFullscreenRoutes.map((route) => (
                <Route key={`full:${route.path}`} path={route.path} element={route.element} />
              ))}
              <Route path="*" element={<ShellRoutes user={user} onLogout={onLogout} />} />
            </Routes>
          )}
        </AuthGate>
  );
}

function ShellRoutes({ user, onLogout }: { user: Parameters<typeof AppShell>[0]['user']; onLogout: () => void }) {
  return (
            <AppShell user={user} onLogout={onLogout}>
              <Routes>
                {featureRoutes.map((route) => (
                  <Route key={`feature:${route.path}`} path={route.path} element={route.element} />
                ))}
                <Route index element={<DashboardPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="reports/:reportKey" element={<ReportViewRoute />} />
                <Route path="health" element={<ResourceHealthPage />} />
                <Route path="probe" element={<ReactAdminProbePage />} />
                <Route path="items" element={<ItemsPage />} />
                <Route path="services" element={<CrudResourcePage config={resourceConfigByName('items-services')!} />} />
                <Route path="recipes" element={<CrudResourcePage config={resourceConfigByName('items-recipes')!} />} />
                <Route path="combos" element={<CombosPage />} />
                <Route path="customers" element={<CrudResourcePage config={resourceConfigByName('customers')!} />} />
                <Route path="suppliers" element={<CrudResourcePage config={resourceConfigByName('suppliers')!} />} />
                <Route path="sales" element={<CrudResourcePage config={resourceConfigByName('sales')!} />} />
                <Route path="orders" element={<CrudResourcePage config={resourceConfigByName('orders')!} />} />
                <Route path="purchases" element={<CrudResourcePage config={resourceConfigByName('purchases')!} />} />
                <Route path="quotations" element={<CrudResourcePage config={resourceConfigByName('quotations')!} />} />
                <Route path="return-orders" element={<CrudResourcePage config={resourceConfigByName('return_orders')!} />} />
                <Route path="deliveries" element={<CrudResourcePage config={resourceConfigByName('deliveries')!} />} />
                <Route path="accounts" element={<CrudResourcePage config={resourceConfigByName('accounts')!} />} />
                <Route path="payments" element={<CrudResourcePage config={resourceConfigByName('payments')!} />} />
                <Route path="incomes" element={<CrudResourcePage config={resourceConfigByName('incomes')!} />} />
                <Route path="expenses" element={<CrudResourcePage config={resourceConfigByName('expenses')!} />} />
                <Route path="stock-adjustments" element={<CrudResourcePage config={resourceConfigByName('adjustments')!} />} />
                <Route path="stock-transfers" element={<CrudResourcePage config={resourceConfigByName('transfers/stock')!} />} />
                <Route path="asset-transfers" element={<CrudResourcePage config={resourceConfigByName('transfers/asset')!} />} />
                <Route path="register-records" element={<CrudResourcePage config={resourceConfigByName('reports/registers')!} />} />
                <Route path="users" element={<CrudResourcePage config={resourceConfigByName('users')!} />} />
                <Route path="roles" element={<CrudResourcePage config={resourceConfigByName('roles')!} />} />
                <Route path="locations" element={<LocationsPage />} />
                <Route path="price-groups" element={<CrudResourcePage config={resourceConfigByName('price_groups')!} />} />
                <Route path="customer-groups" element={<CrudResourcePage config={resourceConfigByName('customer_groups')!} />} />
                <Route path="recurring-sales" element={<CrudResourcePage config={resourceConfigByName('recurring_sales')!} />} />
                <Route path="gift-cards" element={<CrudResourcePage config={resourceConfigByName('gift_cards')!} />} />
                <Route path="promotions" element={<CrudResourcePage config={resourceConfigByName('promos')!} />} />
                <Route path="brands" element={<CrudResourcePage config={resourceConfigByName('brands')!} />} />
                <Route path="units" element={<CrudResourcePage config={resourceConfigByName('units')!} />} />
                <Route path="categories" element={<CrudResourcePage config={resourceConfigByName('categories')!} />} />
                <Route path="taxes" element={<CrudResourcePage config={resourceConfigByName('taxes')!} />} />
                <Route path="halls" element={<CrudResourcePage config={resourceConfigByName('halls')!} />} />
                <Route path="fields" element={<CrudResourcePage config={resourceConfigByName('fields')!} />} />
                <Route path="modifiers" element={<CrudResourcePage config={resourceConfigByName('modifiers')!} />} />
                <Route path="events" element={<CrudResourcePage config={resourceConfigByName('events')!} />} />
                <Route path="tables" element={<CrudResourcePage config={resourceConfigByName('tables')!} />} />
                <Route path="merchants" element={<CrudResourcePage config={resourceConfigByName('merchants')!} />} />
                <Route path="salaries" element={<CrudResourcePage config={resourceConfigByName('salaries')!} />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="label-design" element={<Suspense fallback={<LoadingBlock />}><LabelDesignPage /></Suspense>} />
                <Route path="payment-providers" element={<PaymentProvidersPage />} />
                <Route path="payment-gateways" element={<CrudResourcePage config={resourceConfigByName('payment_gateways')!} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
  );
}
